import { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { CheckCircle2, ChevronDown } from 'lucide-react'
import ComplianceDeadlineBanner from '@/components/collaboration-detail/compliance/ComplianceDeadlineBanner'
import ContractScanPanel from '@/components/collaboration-detail/compliance/ContractScanPanel'
import NilGoSubmissionForm from '@/components/collaboration-detail/compliance/NilGoSubmissionForm'
import NilGoConfirmationInput from '@/components/collaboration-detail/compliance/NilGoConfirmationInput'
import InstitutionalDisclosurePanel from '@/components/collaboration-detail/compliance/InstitutionalDisclosurePanel'
import {
  useUpdateCollaborationMutation,
  useScanContractMutation,
  collaborationsApi,
} from '@/redux/collaborationsApi'
import { uploadCollaborationContract } from '@/redux/documentsSlice'
import type { RootState, AppDispatch } from '@/store'

interface Props {
  campaign: any
}

function addBusinessDays(startDate: Date, days: number): Date {
  const result = new Date(startDate)
  let added = 0
  while (added < days) {
    result.setDate(result.getDate() + 1)
    const dayOfWeek = result.getDay()
    if (dayOfWeek !== 0 && dayOfWeek !== 6) added++
  }
  return result
}

// ─── Types ───────────────────────────────────────────────────────────────────

type PillStatus = 'done' | 'pending' | 'action' | 'na'

// ─── Status Strip ────────────────────────────────────────────────────────────

function StatusPill({
  label,
  status,
  statusText,
  onClick,
}: {
  label: string
  status: PillStatus
  statusText: string
  onClick: () => void
}) {
  const dotColor =
    status === 'done'
      ? 'bg-[#C0FD72]'
      : status === 'action'
        ? 'bg-[#FFA07A]'
        : status === 'pending'
          ? 'bg-[#F9DA4C]'
          : 'bg-white/20'

  const textColor =
    status === 'done'
      ? 'text-[#C0FD72]'
      : status === 'action'
        ? 'text-[#FFA07A]'
        : status === 'pending'
          ? 'text-[#F9DA4C]'
          : 'text-white/30'

  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 bg-white/6 border border-white/10 hover:border-white/25 active:scale-95 rounded-full px-3 py-1.5 shrink-0 transition-all cursor-pointer"
    >
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotColor}`} />
      <span className="text-white/50 text-xs whitespace-nowrap">{label}</span>
      <span className={`text-xs font-semibold whitespace-nowrap ${textColor}`}>{statusText}</span>
    </button>
  )
}

function ComplianceStatusStrip({
  scanResult,
  nilGoStatus,
  confirmationNumber,
  institutionalStatus,
  onPillClick,
}: {
  scanResult: 'clean' | 'flagged' | 'pending' | 'not_scanned'
  nilGoStatus: string
  confirmationNumber: string
  institutionalStatus: string
  onPillClick: (section: 'scan' | 'form' | 'confirm' | 'disclosure') => void
}) {
  const scanPill: { status: PillStatus; text: string } =
    scanResult === 'clean'
      ? { status: 'done', text: 'Clean ✓' }
      : scanResult === 'flagged'
        ? { status: 'action', text: 'Flagged' }
        : scanResult === 'pending'
          ? { status: 'pending', text: 'Scanning…' }
          : { status: 'na', text: '—' }

  const isConfirmed = confirmationNumber && confirmationNumber !== 'IN_PROGRESS'
  const isInProgress = confirmationNumber === 'IN_PROGRESS'

  const nilGoPill: { status: PillStatus; text: string } =
    nilGoStatus === 'confirmed' || isConfirmed
      ? { status: 'done', text: 'Confirmed ✓' }
      : nilGoStatus === 'in_progress' || isInProgress
        ? { status: 'pending', text: 'In Progress' }
        : { status: 'action', text: 'Not Submitted' }

  const confirmPill: { status: PillStatus; text: string } = isConfirmed
    ? { status: 'done', text: 'Logged ✓' }
    : isInProgress
      ? { status: 'pending', text: 'Pending' }
      : { status: 'na', text: '—' }

  const disclosurePill: { status: PillStatus; text: string } =
    institutionalStatus === 'acknowledged'
      ? { status: 'done', text: 'Acknowledged ✓' }
      : institutionalStatus === 'submitted'
        ? { status: 'done', text: 'Submitted ✓' }
        : { status: 'action', text: 'Not Started' }

  return (
    <div>
      <p className="text-white/30 text-xs mb-2.5 font-medium">Tap a section to jump to it</p>
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
        <StatusPill
          label="Contract"
          status={scanPill.status}
          statusText={scanPill.text}
          onClick={() => onPillClick('scan')}
        />
        <StatusPill
          label="NIL Go"
          status={nilGoPill.status}
          statusText={nilGoPill.text}
          onClick={() => onPillClick('form')}
        />
        <StatusPill
          label="Confirmation"
          status={confirmPill.status}
          statusText={confirmPill.text}
          onClick={() => onPillClick('confirm')}
        />
        <StatusPill
          label="School"
          status={disclosurePill.status}
          statusText={disclosurePill.text}
          onClick={() => onPillClick('disclosure')}
        />
      </div>
    </div>
  )
}

// ─── Collapsed Section Row ───────────────────────────────────────────────────

function CollapsedRow({
  title,
  statusText,
  statusColor,
  onExpand,
}: {
  title: string
  statusText: string
  statusColor: string
  onExpand: () => void
}) {
  return (
    <button
      onClick={onExpand}
      className="w-full flex items-center justify-between bg-white/5 border border-white/10 hover:border-white/25 rounded-2xl px-5 py-4 transition-colors group"
    >
      <div className="flex items-center gap-3 min-w-0">
        <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: statusColor }} />
        <span className="text-white/70 text-sm text-left">{title}</span>
        <span className="text-xs font-semibold" style={{ color: statusColor }}>
          {statusText}
        </span>
      </div>
      <ChevronDown className="w-4 h-4 text-white/30 group-hover:text-white/60 shrink-0 ml-3 transition-colors" />
    </button>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────

const ComplianceTab = ({ campaign }: Props) => {
  const dispatch = useDispatch<AppDispatch>()
  const currentUser = useSelector((state: RootState) => state.user.currentUser)
  const [updateCollaboration] = useUpdateCollaborationMutation()
  const [scanContract] = useScanContractMutation()

  const rawPayout = campaign.payout != null ? parseFloat(campaign.payout) : null
  const payout = rawPayout ?? 0
  const isBelowThreshold =
    rawPayout !== null && !isNaN(rawPayout) && rawPayout > 0 && rawPayout < 600

  const dealExecutedAt =
    campaign.dealExecutedAt ?? campaign.bidReceivedAt ?? new Date().toISOString()
  const nilGoStatus = campaign.nilGoStatus ?? 'not_started'
  const institutionalStatus = campaign.institutionalDisclosureStatus ?? 'not_started'

  const executedDate = new Date(dealExecutedAt)
  const institutionalDeadlineDate = new Date(executedDate)
  institutionalDeadlineDate.setDate(institutionalDeadlineDate.getDate() + 30)
  const institutionalDeadline = institutionalDeadlineDate.toISOString()
  void addBusinessDays(executedDate, 5)

  const school = currentUser?.school ? { name: currentUser.school.name } : null
  const complianceContact = (currentUser?.school as any)?.complianceContact ?? undefined

  const [confirmationNumber, setConfirmationNumber] = useState<string>(
    campaign.nilGoConfirmationNumber ?? '',
  )
  const [submittedAt, setSubmittedAt] = useState<string | undefined>(
    campaign.nilGoSubmittedAt ?? undefined,
  )
  const [isSaving, setIsSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [institutionalDisclosureStatus, setInstitutionalDisclosureStatus] =
    useState(institutionalStatus)
  const [institutionalSubmittedAt, setInstitutionalSubmittedAt] = useState<string | undefined>(
    campaign.institutionalDisclosureSubmittedAt ?? undefined,
  )

  const [scanResults, setScanResults] = useState(
    campaign.contractScanResult ?? {
      scannedAt: new Date().toISOString(),
      overallResult: 'not_scanned' as const,
      flags: [],
    },
  )

  const effectiveNilGoStatus =
    confirmationNumber && confirmationNumber !== 'IN_PROGRESS'
      ? 'confirmed'
      : confirmationNumber === 'IN_PROGRESS'
        ? 'in_progress'
        : nilGoStatus

  // ── Section expand/collapse state ─────────────────────────────────────────
  const [scanExpanded, setScanExpanded] = useState(scanResults.overallResult !== 'clean')
  const [formExpanded, setFormExpanded] = useState(
    !confirmationNumber || confirmationNumber === 'IN_PROGRESS',
  )
  const [disclosureExpanded, setDisclosureExpanded] = useState(
    institutionalStatus === 'not_started',
  )

  // Auto-collapse form once confirmation is saved
  useEffect(() => {
    if (confirmationNumber && confirmationNumber !== 'IN_PROGRESS') {
      setFormExpanded(false)
    }
  }, [confirmationNumber])

  // Auto-collapse disclosure once submitted
  useEffect(() => {
    if (
      institutionalDisclosureStatus === 'submitted' ||
      institutionalDisclosureStatus === 'acknowledged'
    ) {
      setDisclosureExpanded(false)
    }
  }, [institutionalDisclosureStatus])

  // ── Section refs for scroll-to navigation ─────────────────────────────────
  const scanRef = useRef<HTMLDivElement>(null)
  const formRef = useRef<HTMLDivElement>(null)
  const confirmRef = useRef<HTMLDivElement>(null)
  const disclosureRef = useRef<HTMLDivElement>(null)

  const scrollToRef = (ref: React.RefObject<HTMLDivElement>) => {
    // Small delay allows the section to expand before scrolling
    setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 60)
  }

  const handlePillClick = (section: 'scan' | 'form' | 'confirm' | 'disclosure') => {
    switch (section) {
      case 'scan':
        setScanExpanded(true)
        scrollToRef(scanRef)
        break
      case 'form':
        setFormExpanded(true)
        scrollToRef(formRef)
        break
      case 'confirm':
        scrollToRef(confirmRef)
        break
      case 'disclosure':
        setDisclosureExpanded(true)
        scrollToRef(disclosureRef)
        break
    }
  }

  const [isScanning, setIsScanning] = useState(false)
  const [isUploading, setIsUploading] = useState(false)

  const handleScan = async () => {
    if (isScanning) return
    setIsScanning(true)
    setScanResults((prev) => ({ ...prev, overallResult: 'pending' as const }))
    try {
      const result = await scanContract(campaign.id).unwrap()
      setScanResults({
        scannedAt: result.scannedAt ?? new Date().toISOString(),
        overallResult: result.overallResult as any,
        flags: result.flags ?? [],
      })
    } catch {
      setScanResults((prev) => ({ ...prev, overallResult: 'not_scanned' as const }))
    } finally {
      setIsScanning(false)
    }
  }

  const handleUpload = async (file: File) => {
    if (file.size > 20 * 1024 * 1024) return
    setIsUploading(true)
    try {
      const result = await dispatch(
        uploadCollaborationContract({ collaborationId: campaign.id, file }),
      )
      if (uploadCollaborationContract.fulfilled.match(result)) {
        dispatch(
          collaborationsApi.util.invalidateTags([
            { type: 'CollaborationDetail', id: campaign.id },
          ]),
        )
        // Auto-scan immediately — the backend already has the document
        await handleScan()
      }
    } finally {
      setIsUploading(false)
    }
  }

  const handleSaveConfirmation = async (number: string) => {
    setIsSaving(true)
    setSaveSuccess(false)
    try {
      await updateCollaboration({
        campaignId: campaign.id,
        updates: {
          nilGoStatus: 'confirmed',
          nilGoConfirmationNumber: number,
          nilGoSubmittedAt: new Date().toISOString(),
        },
      }).unwrap()
      setConfirmationNumber(number)
      setSubmittedAt(new Date().toISOString())
      setSaveSuccess(true)
    } catch (err) {
      console.error('Failed to save confirmation number', err)
    } finally {
      setIsSaving(false)
    }
  }

  const handleMarkInstitutionalSubmitted = async () => {
    try {
      await updateCollaboration({
        campaignId: campaign.id,
        updates: {
          institutionalDisclosureStatus: 'submitted',
          institutionalDisclosureSubmittedAt: new Date().toISOString(),
        },
      }).unwrap()
      setInstitutionalDisclosureStatus('submitted')
      setInstitutionalSubmittedAt(new Date().toISOString())
    } catch (err) {
      console.error('Failed to mark institutional disclosure submitted', err)
    }
  }

  if (isBelowThreshold) {
    return (
      <div className="space-y-6">
        <ComplianceDeadlineBanner
          dealExecutedAt={dealExecutedAt}
          paymentAmount={payout}
          nilGoStatus="not_required"
          institutionalStatus="not_required"
          school={school}
        />
        <div className="bg-white/10 border border-white/10 rounded-2xl p-6">
          <p className="text-white/60 text-sm leading-relaxed">
            This deal is below the $600 NCAA reporting threshold and does not require NIL Go
            submission. It has been logged in your compliance record.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Status strip — tappable pills that jump to each section */}
      <ComplianceStatusStrip
        scanResult={scanResults.overallResult}
        nilGoStatus={effectiveNilGoStatus}
        confirmationNumber={confirmationNumber}
        institutionalStatus={institutionalDisclosureStatus}
        onPillClick={handlePillClick}
      />

      {/* 1. Deadline Banner — always shown */}
      <section>
        <ComplianceDeadlineBanner
          dealExecutedAt={dealExecutedAt}
          paymentAmount={payout}
          nilGoStatus={effectiveNilGoStatus as any}
          institutionalStatus={institutionalDisclosureStatus as any}
          school={school}
        />
      </section>

      <div className="border-t border-white/8" />

      {/* 2. Contract Scan */}
      <section ref={scanRef}>
        {!scanExpanded && scanResults.overallResult === 'clean' ? (
          <CollapsedRow
            title="Agent Contract Scan"
            statusText="No issues found ✓"
            statusColor="#C0FD72"
            onExpand={() => setScanExpanded(true)}
          />
        ) : (
          <ContractScanPanel
            scanResults={scanResults}
            contractAvailable={!!campaign.contractUrl}
            onScan={handleScan}
            isScanning={isScanning}
            onUpload={handleUpload}
            isUploading={isUploading}
          />
        )}
      </section>

      <div className="border-t border-white/8" />

      {/* 3. NIL Go Submission Form */}
      <section ref={formRef}>
        {!formExpanded && confirmationNumber && confirmationNumber !== 'IN_PROGRESS' ? (
          <CollapsedRow
            title="NIL Go Submission Form"
            statusText="All fields ready ✓"
            statusColor="#C0FD72"
            onExpand={() => setFormExpanded(true)}
          />
        ) : (
          <NilGoSubmissionForm
            campaign={campaign}
            onSave={async (nilGoFields) => {
              await updateCollaboration({
                campaignId: campaign.id,
                updates: { nilGoFields },
              }).unwrap()
            }}
          />
        )}
      </section>

      <div className="border-t border-white/8" />

      {/* 4. NIL Go Confirmation Input — never collapses, primary action */}
      <section ref={confirmRef}>
        <NilGoConfirmationInput
          confirmationNumber={confirmationNumber}
          onSave={handleSaveConfirmation}
          isSaving={isSaving}
          saveSuccess={saveSuccess}
          submittedAt={submittedAt}
        />
      </section>

      <div className="border-t border-white/8" />

      {/* 5. Institutional Disclosure */}
      <section ref={disclosureRef}>
        {!disclosureExpanded &&
        (institutionalDisclosureStatus === 'submitted' ||
          institutionalDisclosureStatus === 'acknowledged') ? (
          <CollapsedRow
            title="Institutional Disclosure"
            statusText={
              institutionalDisclosureStatus === 'acknowledged'
                ? 'Acknowledged by school ✓'
                : 'School notified ✓'
            }
            statusColor="#C0FD72"
            onExpand={() => setDisclosureExpanded(true)}
          />
        ) : (
          <InstitutionalDisclosurePanel
            school={school}
            institutionalStatus={institutionalDisclosureStatus as any}
            institutionalDeadline={institutionalDeadline}
            institutionalSubmittedAt={institutionalSubmittedAt}
            complianceOfficer={
              complianceContact
                ? {
                    name: complianceContact.name,
                    title: complianceContact.title,
                    email: complianceContact.email,
                    phone: complianceContact.phone,
                  }
                : undefined
            }
            campaign={campaign}
            user={currentUser}
            onMarkSubmitted={handleMarkInstitutionalSubmitted}
          />
        )}
      </section>
    </div>
  )
}

export default ComplianceTab
